import { clstx } from '@payaca/utilities/cvaMerge';
import { Content, useEditor } from '@tiptap/react';
import { useEffect, useRef } from 'react';

import { RichTextEditorVariables } from '@payaca/types/richTextEditorVariables';

import { DEFAULT_TOOLBAR, type EditorToolbarItem } from './editorActions';
import { baseEditorExtensions } from './editorExtensions';
import EditorShell from './EditorShell';
import { VariableNode } from './nodes/variable';
import VariableList from './VariableList';

import type { JSX } from 'react';

export type RichTextareaToolbarItem = EditorToolbarItem;

const extensions = [...baseEditorExtensions, VariableNode];

type OnChangeType<IsHtmlOutput extends boolean> = IsHtmlOutput extends true
  ? string
  : object | object[] | null;

export type RichTextContent = Content;

export interface RichTextareaProps<
  T extends string,
  IsHtmlOutput extends boolean,
> {
  value: RichTextContent;
  onChange?: (value: OnChangeType<IsHtmlOutput>) => void;
  variables?: RichTextEditorVariables<T>;
  readOnly?: boolean;
  isHtmlOutput?: IsHtmlOutput;
  className?: string;
  /**
   * Pass `false` to hide the toolbar entirely. Pass an array to customise the
   * toolbar items. Defaults to `DEFAULT_TOOLBAR`.
   */
  toolbar?: RichTextareaToolbarItem[] | false;
}

const RichTextarea: {
  (props: RichTextareaProps<string, true>): JSX.Element;
  (props: RichTextareaProps<string, false>): JSX.Element;
} = <T extends string, IsHtmlOutput extends boolean>(
  props: RichTextareaProps<T, IsHtmlOutput>
) => {
  const {
    variables,
    value,
    onChange,
    readOnly = false,
    isHtmlOutput = false,
    className,
    toolbar = DEFAULT_TOOLBAR,
  } = props;

  // Track the last value we emitted via onChange so the value-sync effect
  // can skip the controlled-component echo path. Compared by JSON to handle
  // both string (HTML) and object (TipTap JSON) outputs uniformly.
  const lastEmittedJsonRef = useRef<string>(JSON.stringify(value));

  const editor = useEditor({
    // v3's useEditor does not re-render the component on transactions unless
    // this is set, so the toolbar would otherwise freeze its isActive/disabled
    // states at the initial selection.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor }) => {
      const v = isHtmlOutput ? editor.getHTML() : editor.getJSON();
      lastEmittedJsonRef.current = JSON.stringify(v);
      onChange?.(v as OnChangeType<IsHtmlOutput>);
    },
    extensions,
    content: value,
    editorProps: {
      attributes: {
        class: clstx(
          'tiptap-editor',
          'typography:prose focus:outline-none',
          !readOnly ? 'p-3' : '',
          className
        ),
      },
    },
    editable: !readOnly,
  });

  // Toggle editability without rebuilding the editor — preserves undo
  // history, focus, and scroll position when readOnly flips at runtime.
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== !readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // Sync external `value` changes (e.g. form reset, switching the
  // displayed entity) into the editor. Skip the controlled-component echo
  // path and skip when the editor already matches.
  useEffect(() => {
    if (!editor) return;
    const incoming = JSON.stringify(value);
    if (incoming === lastEmittedJsonRef.current) return;
    const current = isHtmlOutput ? editor.getHTML() : editor.getJSON();
    if (JSON.stringify(current) === incoming) return;
    editor.commands.setContent(value, { emitUpdate: false });
    lastEmittedJsonRef.current = incoming;
  }, [editor, value, isHtmlOutput]);

  return (
    <EditorShell
      editor={editor}
      readOnly={readOnly}
      toolbar={toolbar}
      sidebar={
        variables?.length ? (
          <VariableList<T>
            editor={editor}
            variables={variables}
            className="md:w-1/4"
          />
        ) : undefined
      }
    />
  );
};

export default RichTextarea;
